import { DeleteIcon } from '@chakra-ui/icons';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box,
    Button,
    Flex,
    IconButton,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import { CampaignDto } from '../../../models/campaign/dto/campaign.dto';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import ErrorState from '../../../components/states/ErrorState';
import { formatDate, RULE_TYPE_LABELS, STATUS_COLORS } from '../utils/campaign.utils';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useDeleteCampaign } from '../hooks/useDeleteCampaign';
import { useListCampaigns } from '../hooks/useListCampaigns';
import CreateCampaignModal from './CreateCampaignModal';

const columnHelper = createColumnHelper<CampaignDto>();

export default function CampaignList() {
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isInitialLoading, isError } = useListCampaigns(selectedOrganization?.id || '');
    // `useListCampaigns` is `enabled: !!organizationId` and this page carried NO
    // scope guard, so the raw `isLoading` it used to spin on stayed `true`
    // forever whenever no organization was selected — React Query v4 pins a
    // disabled query at `isLoading: true`. Classify off the query's own flags
    // rather than re-deriving the gate from the store (D62).
    const readState = classifyRead(isError, isInitialLoading, data);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const toast = useToast();
    const navigate = useNavigate();

    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingCampaign, setDeletingCampaign] = useState<CampaignDto | null>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const { mutateAsync: deleteCampaign, isLoading: isDeleting } = useDeleteCampaign();

    const handleDelete = useCallback((campaign: CampaignDto) => {
        setDeletingCampaign(campaign);
        onDeleteOpen();
    }, [onDeleteOpen]);

    const handleConfirmDelete = async () => {
        if (!deletingCampaign) return;
        try {
            await deleteCampaign(deletingCampaign.id);
            toast({
                title: translate('campaign.deleted_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onDeleteClose();
            setDeletingCampaign(null);
        } catch (error: any) {
            toast({
                title: translate('campaign.delete_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('campaign.name')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm' fontWeight='700'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('campaign.status')}
                </Text>
            ),
            cell: (info) => (
                <Badge colorScheme={STATUS_COLORS[info.getValue()]}>
                    {info.getValue()}
                </Badge>
            ),
        }),
        columnHelper.accessor('rules', {
            id: 'rules',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('campaign.rules')}
                </Text>
            ),
            cell: (info) => (
                <Flex gap={1} wrap="wrap">
                    {info.getValue().map((rule) => (
                        <Badge key={rule.id} size="sm" colorScheme="brand" fontSize="xs">
                            {RULE_TYPE_LABELS[rule.type] || rule.type}: {rule.value}d
                        </Badge>
                    ))}
                </Flex>
            ),
        }),
        columnHelper.accessor('schedule', {
            id: 'schedule',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('campaign.schedule')}
                </Text>
            ),
            cell: (info) => {
                const s = info.getValue();
                return (
                    <Text color={textColor} fontSize='sm'>
                        {s.frequency} @ {s.hour}:00 ({s.timezone})
                    </Text>
                );
            },
        }),
        columnHelper.accessor('nextRunAt', {
            id: 'nextRunAt',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('campaign.next_run')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm'>
                    {info.getValue() ? formatDate(info.getValue()) : '-'}
                </Text>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('campaign.actions')}
                </Text>
            ),
            cell: (info) => (
                <Flex align='center' gap={2}>
                    <IconButton
                        aria-label={translate('campaign.delete')}
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(info.row.original);
                        }}
                    />
                </Flex>
            ),
        }),
    ], [textColor, handleDelete]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (readState === 'never-issued') {
        return (
            <PageCard title={translate('campaign.campaigns')}>
                <ScopePrompt gate='organization' />
            </PageCard>
        );
    }

    if (readState === 'loading') {
        return <Loader isLoading />;
    }

    // `unreadable`, not the raw `isError`: a BACKGROUND refetch that fails over
    // rows already on screen must keep them rather than blank the table (R96-3).
    if (readState === 'unreadable') {
        return (
            <PageCard title={translate('campaign.campaigns')}>
                <ErrorState message={translate('campaign.error_loading')} />
            </PageCard>
        );
    }

    return (
        <PageCard
            title={translate('campaign.campaigns')}
            rightHeader={
                <Button colorScheme="brand" onClick={onCreateOpen} size="sm">
                    {translate('campaign.create_campaign')}
                </Button>
            }
        >
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <Th
                                        {...tableThProps}
                                        key={header.id}
                                        borderColor={borderColor}
                                        cursor='pointer'
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <Flex
                                            {...tableThFlexProps}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <SortIndicator direction={header.column.getIsSorted()} />
                                        </Flex>
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map(row => (
                            <Tr
                                key={row.id}
                                {...tableRowProps}
                                onClick={() => navigate(`/admin/campaigns/${row.original.id}`)}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <Td
                                        key={cell.id}
                                        {...tableCellProps}
                                        borderColor={borderColor}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
                {(!data || data.length === 0) && (
                    <Flex justify="center" py={8}>
                        <Text color="gray.400">{translate('campaign.no_campaigns')}</Text>
                    </Flex>
                )}
            </Box>

            <CreateCampaignModal isOpen={isCreateOpen} onClose={onCreateClose} />

            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelButtonRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {translate('campaign.delete')}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('campaign.delete_confirm').replace('{name}', deletingCampaign?.name || '')}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                                {translate('common.cancel')}
                            </Button>
                            <Button colorScheme='red' onClick={handleConfirmDelete} ml={3} isLoading={isDeleting}>
                                {translate('common.delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </PageCard>
    );
}
